'use client';
import { useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, post, event, analyticsStatus } from '@curio/client';
import { Button, ErrorMessage } from '@curio/ui';
import { Turnstile, type SecurityCheck } from './turnstile';
import { GitHubIcon, GoogleIcon } from './provider-icons';
const providers = [
  ['google', 'Google', <GoogleIcon key="google" />],
  ['github', 'GitHub', <GitHubIcon key="github" />],
] as const;
// Codes Better Auth appends to the error callback URL after a provider round
// trip. Anything unlisted gets the generic retry message.
const oauthErrors: Record<string, string> = {
  account_not_linked:
    'An account with this email already exists but has not been verified yet. Open the verification link in your inbox, then try again.',
};
function oauthError(code: string | null) {
  if (!code) return undefined;
  return new Error(
    oauthErrors[code] ?? 'Sign-in did not complete. Please try again.',
  );
}
export function SignIn() {
  const router = useRouter();
  const params = useSearchParams();
  const client = useQueryClient();
  const [mode, setMode] = useState<'sign-in' | 'sign-up' | 'forgot' | 'reset'>(
    params.get('token') ? 'reset' : 'sign-in',
  );
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(oauthError(params.get('error')));
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const security = useRef<SecurityCheck>(null);
  const cfg = useQuery({
    queryKey: ['config'],
    queryFn: () =>
      api<{
        github: boolean;
        google: boolean;
        preview: boolean;
        turnstileSiteKey: string | null;
      }>('/config'),
  });
  const protectedPost = async <T,>(path: string, body: unknown) => {
    if (!cfg.data || !security.current)
      throw Error('Sign-in options are still loading. Please try again.');
    const token = await security.current.token();
    try {
      return await api<T>(path, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'x-turnstile-token': token },
      });
    } finally {
      security.current.reset();
    }
  };
  const next = params.get('next');
  const destination =
    next?.startsWith('/') && !next.startsWith('//') ? next : '/builder';
  const finish = async () => {
    await client.invalidateQueries();
    router.push(destination);
  };
  const startSocial = async (provider: 'github' | 'google') => {
    const flow = mode === 'sign-up' ? 'sign_up' : 'sign_in';
    setBusy(true);
    setError(undefined);
    event('authentication_started', undefined, { method: provider, flow });
    try {
      const r = await protectedPost<{ url: string }>('/auth/sign-in/social', {
        provider,
        callbackURL: location.origin + destination,
        // Better Auth appends ?error=<code>; keep the URL clean so the code
        // is what the page reads back.
        errorCallbackURL: location.origin + '/sign-in',
      });
      location.assign(r.url);
    } catch (e) {
      event('authentication_failed', undefined, {
        method: provider,
        flow,
        status_code: analyticsStatus(e),
      });
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const available = providers.filter(([id]) => cfg.data?.[id]);
  return (
    <div className="page">
      <div className="auth-width panel">
        <h1>
          {mode === 'sign-up'
            ? 'Create an account'
            : mode === 'forgot'
              ? 'Reset your password'
              : mode === 'reset'
                ? 'Choose a new password'
                : 'Sign in'}
        </h1>
        <form
          className="form"
          onSubmit={async (e) => {
            e.preventDefault();
            setError(undefined);
            setBusy(true);
            const flow =
              mode === 'forgot'
                ? 'password_reset_request'
                : mode === 'reset'
                  ? 'password_reset'
                  : mode === 'sign-up'
                    ? 'sign_up'
                    : 'sign_in';
            event('authentication_started', undefined, {
              method: 'email',
              flow,
            });
            try {
              if (mode === 'forgot') {
                await protectedPost('/auth/request-password-reset', {
                  email,
                  redirectTo: location.origin + '/sign-in',
                });
                event('password_reset_requested', undefined, {});
                setMessage(
                  'If that email has an account, a reset link is on its way.',
                );
              } else if (mode === 'reset') {
                await post('/auth/reset-password', {
                  newPassword: password,
                  token: params.get('token'),
                });
                event('password_reset_completed', undefined, {});
                setMessage('Password updated. You can sign in now.');
                setMode('sign-in');
              } else if (mode === 'sign-up') {
                await protectedPost('/auth/sign-up/email', {
                  email,
                  name,
                  password,
                  callbackURL: location.origin + destination,
                });
                setMessage('Check your email to verify your account.');
              } else {
                await post('/auth/sign-in/email', { email, password });
                await finish();
              }
            } catch (e) {
              event('authentication_failed', undefined, {
                method: 'email',
                flow,
                status_code: analyticsStatus(e),
              });
              setError(e);
            } finally {
              setBusy(false);
            }
          }}
        >
          {mode === 'sign-up' && (
            <label>
              Your name
              <input
                required
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
          )}
          {mode !== 'reset' && (
            <label>
              Email
              <input
                required
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
          )}
          {mode !== 'forgot' && (
            <label>
              Password
              <input
                required
                type="password"
                minLength={10}
                autoComplete={
                  mode === 'sign-in' ? 'current-password' : 'new-password'
                }
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
          )}
          <ErrorMessage error={error} />
          {cfg.isError && (
            <p className="notice" role="alert">
              Sign-in options could not load.{' '}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void cfg.refetch()}
              >
                Retry
              </Button>
            </p>
          )}
          {message && (
            <p className="notice" role="status">
              {message}
            </p>
          )}
          {cfg.data && mode !== 'reset' && (
            <Turnstile
              ref={security}
              siteKey={cfg.data.turnstileSiteKey}
              action={
                mode === 'sign-up'
                  ? 'signup'
                  : mode === 'forgot'
                    ? 'email'
                    : 'social'
              }
            />
          )}
          <Button
            disabled={
              busy || ((mode === 'sign-up' || mode === 'forgot') && !cfg.data)
            }
          >
            {busy
              ? 'Please wait…'
              : mode === 'sign-in'
                ? 'Sign in'
                : mode === 'sign-up'
                  ? 'Create account'
                  : mode === 'reset'
                    ? 'Set password'
                    : 'Send reset link'}
          </Button>
        </form>
        {(mode === 'sign-in' || mode === 'sign-up') &&
          (cfg.isPending || available.length > 0) && (
            <>
              <p className="caption secondary auth-or">or continue with</p>
              {cfg.isPending ? (
                <div
                  className="auth-social loading-reveal"
                  aria-label="Loading sign-in options"
                  role="status"
                >
                  <div className="skeleton" style={{ height: 38 }} />
                  <div className="skeleton" style={{ height: 38 }} />
                </div>
              ) : (
                <div className="auth-social">
                  {available.map(([id, label, icon]) => (
                    <Button
                      variant="outline"
                      key={id}
                      disabled={busy}
                      icon={icon}
                      aria-label={`Continue with ${label}`}
                      onClick={() => void startSocial(id)}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              )}
            </>
          )}
        <div className="split" style={{ marginTop: 18 }}>
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => setMode(mode === 'sign-up' ? 'sign-in' : 'sign-up')}
          >
            {mode === 'sign-up'
              ? 'Already have an account?'
              : 'Create an account'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => setMode(mode === 'forgot' ? 'sign-in' : 'forgot')}
          >
            {mode === 'forgot' ? 'Back to sign in' : 'Forgot password?'}
          </Button>
        </div>
        {cfg.data?.preview && (
          <div className="stack" style={{ marginTop: 16, gap: 8 }}>
            {(
              [
                ['1', 'Use isolated preview account'],
                ['builder', 'Use preview account with activity'],
              ] as const
            ).map(([persona, label]) => (
              <Button
                key={persona}
                variant="secondary"
                full
                onClick={() => {
                  document.cookie = `curio_preview=${persona}; Path=/; SameSite=Lax`;
                  void finish();
                }}
              >
                {label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
